/**
 * Browser side of the device console channel.
 *
 * Speaks `json.webpubsub.azure.v1` over a native WebSocket — the same wire
 * format the device agent uses (see docs/agent/PROTOCOL.md). Deliberately no
 * SDK: the protocol we need is a dozen lines, and the agent already implements
 * the same subset, so one file here keeps both ends readable side by side.
 *
 * Groups are baked into the access token, so this never sends `joinGroup`.
 *
 * Ordering in this subprotocol is per-connection, not global, so consumers that
 * care about gaps must look at `seq` rather than assume arrival order.
 */

const SUBPROTOCOL = "json.webpubsub.azure.v1";
const PROTOCOL_VERSION = 1;
const BACKOFF_MIN_MS = 1000;
const BACKOFF_MAX_MS = 30000;

export function envelope(ch, t, d = {}, extra = {}) {
  return { v: PROTOCOL_VERSION, ch, t, ts: Date.now() / 1000, d, ...extra };
}

export class DeviceChannel {
  /**
   * @param {object} opts
   * @param {() => Promise<object>} opts.fetchToken  mints a fresh console token
   * @param {(env: object, fromUserId: string|null) => void} opts.onEnvelope
   * @param {(status: string, detail?: any) => void} [opts.onStatus]
   */
  constructor({ fetchToken, onEnvelope, onStatus }) {
    this.fetchToken = fetchToken;
    this.onEnvelope = onEnvelope;
    this.onStatus = onStatus || (() => {});
    this.ws = null;
    this.groups = [];
    this.capabilities = [];
    this.closed = false;
    this.backoff = BACKOFF_MIN_MS;
    this._retryTimer = null;
    // sid -> {resolve, reject, timer} for in-flight command requests.
    this._pending = new Map();
    // The terminal subscribes here rather than through onEnvelope: it mounts
    // and unmounts independently of the page that owns the channel.
    this._ptyHandlers = new Set();
  }

  get connected() {
    return !!this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  get controlGroup() {
    return this.groups.find((g) => g.endsWith(".ctl")) || null;
  }

  get logGroup() {
    return this.groups.find((g) => g.endsWith(".logs")) || null;
  }

  /** Present only when the token was granted the `pty` capability. */
  get ptyGroup() {
    return this.groups.find((g) => g.endsWith(".pty")) || null;
  }

  async connect() {
    this.closed = false;
    let token;
    try {
      token = await this.fetchToken();
    } catch (err) {
      this.onStatus("error", err);
      return this._scheduleRetry();
    }

    // The mock API has no real socket behind it; say so rather than failing in
    // a way that looks like the device is unreachable.
    if (!token || !token.url) {
      this.onStatus("unavailable", token);
      return;
    }

    this.groups = token.groups || [];
    this.capabilities = token.capabilities || [];
    this.onStatus("connecting");

    let ws;
    try {
      ws = new WebSocket(token.url, SUBPROTOCOL);
    } catch (err) {
      this.onStatus("error", err);
      return this._scheduleRetry();
    }
    this.ws = ws;

    ws.onopen = () => {
      this.backoff = BACKOFF_MIN_MS;
      this.onStatus("open");
      // Ask the agent to re-announce; its hello carries version, mode, free
      // disk and the command list this device actually supports.
      this.publish(this.controlGroup, envelope("sys", "who"));
    };

    ws.onmessage = (event) => this._handleFrame(event.data);

    ws.onclose = () => {
      this.ws = null;
      this.onStatus("closed");
      if (!this.closed) this._scheduleRetry();
    };

    ws.onerror = (err) => this.onStatus("error", err);
  }

  _handleFrame(raw) {
    let frame;
    try {
      frame = JSON.parse(raw);
    } catch {
      return;
    }
    if (frame.type === "system") {
      this.onStatus(frame.event === "connected" ? "ready" : "system", frame);
      return;
    }
    if (frame.type !== "message") return;
    const data = frame.data;
    if (!data || typeof data !== "object") return;
    // fromUserId is stamped by the service from the token, not by the sender,
    // so it is trustworthy attribution.
    this.onEnvelope(data, frame.fromUserId || null);
  }

  _scheduleRetry() {
    if (this.closed || this._retryTimer) return;
    // Jitter: a fleet of consoles reconnecting in lockstep is self-inflicted load.
    const delay = Math.min(this.backoff, BACKOFF_MAX_MS) * (0.5 + Math.random());
    this._retryTimer = setTimeout(() => {
      this._retryTimer = null;
      this.backoff = Math.min(this.backoff * 2, BACKOFF_MAX_MS);
      this.connect();
    }, delay);
    this.onStatus("reconnecting", { in_ms: Math.round(delay) });
  }

  publish(group, env) {
    if (!group || !this.connected) return false;
    this.ws.send(
      JSON.stringify({
        type: "sendToGroup",
        group,
        dataType: "json",
        data: env,
        noEcho: true,
      })
    );
    return true;
  }

  /** Run a whitelisted command; resolves with the agent's response envelope. */
  runCommand(command, args = {}, { timeoutMs = 30000 } = {}) {
    const sid = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this._pending.delete(sid);
        reject(new Error(`no response from device within ${Math.round(timeoutMs / 1000)}s`));
      }, timeoutMs);
      this._pending.set(sid, { resolve, reject, timer });
      const ok = this.publish(this.controlGroup, envelope("cmd", "req", { command, args }, { sid }));
      if (!ok) {
        clearTimeout(timer);
        this._pending.delete(sid);
        reject(new Error("channel is not connected"));
      }
    });
  }

  addPtyHandler(fn) {
    this._ptyHandlers.add(fn);
  }

  removePtyHandler(fn) {
    this._ptyHandlers.delete(fn);
  }

  /** Called by the page for every pty envelope it receives. */
  dispatchPty(env) {
    this._ptyHandlers.forEach((fn) => {
      try {
        fn(env);
      } catch {
        /* one bad handler must not stop the others */
      }
    });
  }

  /** Called by the page when a cmd response arrives. */
  resolveCommand(env) {
    const entry = this._pending.get(env.sid);
    if (!entry) return false;
    clearTimeout(entry.timer);
    this._pending.delete(env.sid);
    entry.resolve(env.d);
    return true;
  }

  subscribeLogs(tail = 200) {
    return this.publish(this.logGroup, envelope("log", "subscribe", { tail }));
  }

  unsubscribeLogs() {
    return this.publish(this.logGroup, envelope("log", "unsubscribe"));
  }

  close() {
    this.closed = true;
    if (this._retryTimer) {
      clearTimeout(this._retryTimer);
      this._retryTimer = null;
    }
    // Stop the device streaming before dropping the socket: the tail also
    // self-expires, but leaving it running wastes the message budget.
    this.unsubscribeLogs();
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        /* already gone */
      }
      this.ws = null;
    }
  }
}

export default DeviceChannel;
